import type { ChainId, TokenSymbol } from '@cryptopay/core';
import { CHAINS, getToken } from '@cryptopay/core';

export interface BrowserChainConfig {
  readonly id: ChainId;
  readonly evmChainId: number;
  readonly name: string;
  readonly rpcUrl: string;
  readonly nativeSymbol: string;
  readonly blockExplorer: string;
}

export function getBrowserChain(id: ChainId): BrowserChainConfig {
  const base = CHAINS[id];
  return {
    id: base.id,
    evmChainId: base.evmChainId,
    name: base.name,
    nativeSymbol: base.nativeSymbol,
    blockExplorer: base.blockExplorer,
    rpcUrl: pickRpc(id),
  };
}

export function tokenFor(chain: ChainId, symbol: TokenSymbol) {
  return getToken(chain, symbol);
}

function pickRpc(id: ChainId): string {
  // Per-chain override via env: `VITE_RPC_POLYGON`, `VITE_RPC_BASE`, etc.
  // Lets each deployment plug in an Alchemy/Infura key without a rebuild.
  const envKey = `VITE_RPC_${id.toUpperCase().replace(/-/g, '_')}`;
  const fromEnv = (import.meta.env as Record<string, string | undefined>)[envKey];
  if (fromEnv && fromEnv.trim().length > 0) {
    return fromEnv.trim();
  }
  // Public-node endpoints over polygon-rpc.com / mainnet.base.org: those
  // Cloudflare-fronted defaults intermittently return 401 to browser
  // fetches (bot-protection challenges) and break the balance read.
  // PublicNode/Allnodes endpoints set permissive CORS and don't gate
  // unauth'd requests behind a captcha.
  const defaults: Record<ChainId, string> = {
    polygon: 'https://polygon-bor-rpc.publicnode.com',
    base: 'https://base-rpc.publicnode.com',
    arbitrum: 'https://arbitrum-one-rpc.publicnode.com',
    optimism: 'https://optimism-rpc.publicnode.com',
    bsc: 'https://bsc-rpc.publicnode.com',
    avalanche: 'https://avalanche-c-chain-rpc.publicnode.com',
    'polygon-amoy': 'https://polygon-amoy-bor-rpc.publicnode.com',
    'base-sepolia': 'https://base-sepolia-rpc.publicnode.com',
    'arbitrum-sepolia': 'https://arbitrum-sepolia-rpc.publicnode.com',
  };
  // ChainId union is exhaustive over `defaults`, so the lookup always
  // resolves. Index-access types stay nullable under
  // `noUncheckedIndexedAccess`, so we assert here rather than letting
  // the type leak into every caller.
  const url = defaults[id];
  if (!url) {
    throw new Error(`pickRpc: no default RPC for chain ${id}`);
  }
  return url;
}
